import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  X, Printer, Download, Clock, User, DollarSign, 
  TrendingUp, ShoppingCart, Gamepad2, Calendar, AlertCircle 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ShiftReportDialog({ open, onClose, shift }) {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [businessProfile, setBusinessProfile] = useState(null);
  const printRef = useRef(null);

  useEffect(() => {
    if (open && shift) {
      fetchShiftReport();
      fetchBusinessProfile();
    }
  }, [open, shift]);

  const fetchBusinessProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/business-profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setBusinessProfile(response.data.profile);
    } catch (error) {
      console.error('Error fetching business profile:', error);
    }
  };

  const fetchShiftReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // DEBUG: Confirm kode baru loaded
      console.log('[ShiftReport] CODE VERSION: 2025-12-06-01:40 - use_shift_time FIX');
      
      // Fetch transactions during this shift
      const startDate = new Date(shift.start_time);
      const endDate = shift.end_time ? new Date(shift.end_time) : new Date();
      
      // Get shift's branch_id for filtering
      const shiftBranchId = shift.branch_id;
      
      const response = await axios.get(`${API_URL}/api/transactions/unified`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: {
          date_filter: 'custom',
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
          type_filter: 'all',
          branch_id: shiftBranchId,  // CRITICAL: Filter by shift's branch!
          cashier_username: shift.username,  // CRITICAL: Filter by this shift's kasir!
          use_shift_time: true,  // CRITICAL: Use direct time range for Tutup Kasir!
          only_paid: true,  // CRITICAL: Only fetch PAID transactions for shift report
          limit: 1000
        }
      });

      const transactions = response.data.transactions || [];
      
      console.log('[ShiftReport] ===== API RESPONSE =====');
      console.log('[ShiftReport] Total transactions from backend:', transactions.length);
      console.log('[ShiftReport] API params:', {
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        branch_id: shiftBranchId,
        only_paid: true
      });
      
      console.log('[ShiftReport] Total transactions fetched:', transactions.length);
      console.log('[ShiftReport] Shift period:', shift.start_time, 'to', shift.end_time);
      console.log('[ShiftReport] Shift user:', shift.username, 'branch:', shift.branch);

      // IMPORTANT: Transactions are already filtered by shift TIME period
      // Since only ONE kasir can operate during a shift (shift management ensures this),
      // ALL transactions in this time period belong to this shift's kasir
      // We still try to match operator_name, but if operator_name is "Unknown" (old data),
      // we accept ALL transactions in the time period as they must belong to this shift
      
      // CRITICAL FIX: NEVER use stored data, always calculate from actual transactions
      // Stored data might be incorrect due to bugs during shift close
      // Always fetch and calculate fresh data from transactions
      const useStoredData = false;  // ALWAYS recalculate from transactions
      
      // Count paid vs unpaid
      const paidCount = transactions.filter(t => t.payment_status === 'paid').length;
      const unpaidCount = transactions.filter(t => t.payment_status !== 'paid').length;
      console.log('[ShiftReport] Transactions breakdown - Paid:', paidCount, 'Unpaid:', unpaidCount);
      console.log('[ShiftReport] Use stored data from shift close:', useStoredData);
      
      // Backend already filtered by branch_id and time window with only_paid=true
      // We just need to ensure data consistency and optionally filter by kasir
      console.log('[ShiftReport] ===== FRONTEND FILTERING =====');
      console.log('[ShiftReport] Shift details:', {
        username: shift.username,
        branch: shift.branch,
        branch_id: shift.branch_id,
        start_time: shift.start_time,
        status: shift.status
      });
      
      const shiftTransactions = transactions.filter(t => {
        // Safety check: Only include PAID transactions (should already be filtered by backend)
        if (t.payment_status !== 'paid') {
          console.log('[ShiftReport]  UNPAID transaction found (should not happen):', t.transaction_type, t.payment_method);
          return false;
        }
        
        // CRITICAL: Strict branch_id comparison to prevent cross-branch contamination
        const transactionBranchId = t.branch_id;
        const shiftBranchId = shift.branch_id;
        
        if (!transactionBranchId) {
          console.log('[ShiftReport]  Transaction missing branch_id:', t.transaction_type, t.payment_method);
          return false;
        }
        
        if (transactionBranchId !== shiftBranchId) {
          console.log('[ShiftReport]  Cross-branch transaction excluded:', t.branch, '(ID:', transactionBranchId, ') vs shift:', shift.branch, '(ID:', shiftBranchId, ')');
          return false;
        }
        
        // All checks passed - include this transaction
        return true;
      });
      
      console.log('[ShiftReport] ===== FILTERING RESULTS =====');
      console.log('[ShiftReport] After filtering:', shiftTransactions.length, 'transactions');
      console.log('[ShiftReport] Filtered out:', transactions.length - shiftTransactions.length, 'transactions');
      
      console.log('[ShiftReport] Filtered shift transactions:', shiftTransactions.length);
      console.log('[ShiftReport] Payment methods in filtered transactions:', shiftTransactions.map(t => t.payment_method));

      // Calculate payment method breakdown
      // Use stored data for closed shifts to ensure accuracy
      const paymentBreakdown = useStoredData ? {
        cash: shift.cash_revenue || 0,
        qris: shift.non_cash_revenue || 0,  // For now, non_cash = qris
        transfer: 0
      } : {
        cash: 0,
        qris: 0,
        transfer: 0
      };

      // If not using stored data (open shift), calculate from transactions
      if (!useStoredData) {
        shiftTransactions.forEach(t => {
          const method = (t.payment_method || 'cash').toLowerCase();
          
          // CRITICAL: Use correct field based on transaction type
          let amount = 0;
          if (t.transaction_type === 'ps') {
            amount = t.final_price || t.total_price || 0;
          } else if (t.transaction_type === 'fnb') {
            // F&B standalone uses 'total' field
            amount = t.total || t.final_price || t.total_price || 0;
          }
          
          console.log('[ShiftReport] Transaction:', t.transaction_type, method, amount, 'paid_status:', t.payment_status);
          
          if (method.includes('qris')) {
            paymentBreakdown.qris += amount;
          } else if (method.includes('transfer') || method.includes('bank')) {
            paymentBreakdown.transfer += amount;
          } else {
            paymentBreakdown.cash += amount;
          }
        });
      }
      
      console.log('[ShiftReport] ===== PAYMENT BREAKDOWN =====');
      console.log('[ShiftReport] Use stored data?', useStoredData);
      console.log('[ShiftReport] Payment breakdown:', paymentBreakdown);
      console.log('[ShiftReport] Total:', paymentBreakdown.cash + paymentBreakdown.qris);

      // Calculate transaction types
      const psTransactions = shiftTransactions.filter(t => t.transaction_type === 'ps');
      const fnbTransactions = shiftTransactions.filter(t => t.transaction_type === 'fnb');
      const psWithFnb = psTransactions.filter(t => t.additional_items && t.additional_items.length > 0);

      // Calculate revenues - PS and F&B separately first
      let psRevenue = 0;
      let fnbRevenue = 0;
      
      psTransactions.forEach(t => {
        const totalPrice = t.final_price || t.total_price || 0;
        
        // Calculate F&B subtotal dari additional_items
        let fnbSubtotal = 0;
        if (t.additional_items && t.additional_items.length > 0) {
          fnbSubtotal = t.additional_items.reduce((sum, item) => {
            const price = item.price || 0;
            const qty = item.qty || item.quantity || 1;
            return sum + (price * qty);
          }, 0);
        }
        
        // PS revenue = total - F&B items
        psRevenue += (totalPrice - fnbSubtotal);
        fnbRevenue += fnbSubtotal;
      });
      
      // Tambahkan F&B standalone transactions
      fnbTransactions.forEach(t => {
        // F&B standalone uses 'total' field, NOT 'final_price' or 'total_price'
        fnbRevenue += (t.total || t.final_price || t.total_price || 0);
      });
      
      // IMPORTANT: Total revenue = PS + F&B (calculated after both are computed)
      const totalRevenue = psRevenue + fnbRevenue;

      // NEW: Aggregate F&B items breakdown
      const fnbItemsBreakdown = {};
      
      // Aggregate F&B from PS rentals (additional_items)
      psTransactions.forEach(t => {
        const items = t.additional_items || [];
        items.forEach(item => {
          const name = item.name || 'Unknown';
          const qty = item.qty || item.quantity || 1;
          const price = item.price || 0;
          const total = price * qty;
          
          if (!fnbItemsBreakdown[name]) {
            fnbItemsBreakdown[name] = { qty: 0, revenue: 0 };
          }
          fnbItemsBreakdown[name].qty += qty;
          fnbItemsBreakdown[name].revenue += total;
        });
      });
      
      // Aggregate F&B from standalone orders
      fnbTransactions.forEach(t => {
        const items = t.items || [];
        items.forEach(item => {
          const name = item.name || item.product_name || 'Unknown';
          const qty = item.qty || item.quantity || 1;
          const price = item.price || 0;
          const total = price * qty;
          
          if (!fnbItemsBreakdown[name]) {
            fnbItemsBreakdown[name] = { qty: 0, revenue: 0 };
          }
          fnbItemsBreakdown[name].qty += qty;
          fnbItemsBreakdown[name].revenue += total;
        });
      });
      
      // NEW: Aggregate PS packages breakdown with total hours
      const psPackagesBreakdown = {};
      
      psTransactions.forEach(t => {
        const mode = t.rental_mode || t.mode || 'Unknown';
        const price = t.final_price || t.total_price || 0;
        
        // Calculate PS-only price (exclude F&B)
        let fnbSubtotal = 0;
        if (t.additional_items && t.additional_items.length > 0) {
          fnbSubtotal = t.additional_items.reduce((sum, item) => {
            return sum + (item.price || 0) * (item.qty || item.quantity || 1);
          }, 0);
        }
        const psOnlyPrice = price - fnbSubtotal;
        
        // Calculate actual duration (in hours)
        let actualHours = 0;
        if (t.start_time && t.actual_end_time) {
          const start = new Date(t.start_time);
          const end = new Date(t.actual_end_time);
          const diffMs = end - start;
          
          // Subtract pause time if available
          const pauseMinutes = t.total_pause_minutes || 0;
          const actualMs = diffMs - (pauseMinutes * 60 * 1000);
          
          actualHours = actualMs / (1000 * 60 * 60); // Convert to hours
        } else {
          // Fallback: use duration field for paket
          actualHours = t.duration || 0;
        }
        
        // Map mode to friendly name with UNIQUE key for each duration
        let modeName = mode;
        const duration = t.duration || 0;
        
        if (mode === 'no_limit') {
          modeName = 'No Limit';
        } else if (mode === 'paket') {
          // IMPORTANT: Breakdown per duration (1 jam, 2 jam, 3 jam, etc)
          modeName = `Paket ${duration} Jam`;
        } else if (mode === 'durasi') {
          // IMPORTANT: For durasi mode, use actual hours as duration (rounded)
          const roundedHours = Math.ceil(actualHours); // Round up to nearest hour
          modeName = `Durasi ${roundedHours} Jam`;
        }
        
        if (!psPackagesBreakdown[modeName]) {
          psPackagesBreakdown[modeName] = { qty: 0, revenue: 0, totalHours: 0 };
        }
        psPackagesBreakdown[modeName].qty += 1;
        psPackagesBreakdown[modeName].revenue += psOnlyPrice;
        psPackagesBreakdown[modeName].totalHours += actualHours;
      });

      // CRITICAL FIX: NEVER use saved data from shift document!
      // Always calculate from actual transactions to ensure accuracy
      // Stored data might be incorrect due to bugs in shift close logic
      const useSavedData = false;  // ALWAYS recalculate!
      
      console.log('[ShiftReport] ===== FINAL CALCULATED DATA =====');
      console.log('[ShiftReport] Total Transactions:', shiftTransactions.length);
      console.log('[ShiftReport] Total Revenue:', totalRevenue);
      console.log('[ShiftReport] PS Revenue:', psRevenue);
      console.log('[ShiftReport] FNB Revenue:', fnbRevenue);
      console.log('[ShiftReport] STORED DATA IGNORED! shift.total_revenue:', shift.total_revenue, '(NOT USED)');
      console.log('[ShiftReport] =====================================');
      
      setReportData({
        shift,
        transactions: shiftTransactions,
        summary: {
          // ALWAYS calculate from transactions, NEVER use stored data
          totalTransactions: shiftTransactions.length,
          psTransactions: psTransactions.length,
          fnbTransactions: fnbTransactions.length,
          psWithFnb: psWithFnb.length,
          totalRevenue,  // Calculated from transactions
          psRevenue,     // Calculated from transactions
          fnbRevenue     // Calculated from transactions
        },
        paymentBreakdown,
        fnbItemsBreakdown,  // NEW: F&B items breakdown
        psPackagesBreakdown  // NEW: PS packages breakdown
      });

    } catch (error) {
      console.error('Error fetching shift report:', error);
      toast.error('Gagal memuat laporan shift');
    } finally {
      setLoading(false);
    }
  };

  if (!open || !shift) return null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString('id-ID', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Jakarta' // GMT+7
    });
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleTimeString('id-ID', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Jakarta' // GMT+7
    });
  };

  const calculateDuration = (startTime, endTime) => {
    if (!endTime) return 'Aktif';
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMs = end - start;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours} jam ${minutes} menit`;
  };

  const handlePrint = () => {
    // Get all stylesheets dari parent document (including Tailwind)
    const stylesheets = Array.from(document.styleSheets)
      .map(styleSheet => {
        try {
          return Array.from(styleSheet.cssRules)
            .map(rule => rule.cssText)
            .join('\n');
        } catch (e) {
          // Cross-origin stylesheets might throw error, skip them
          return '';
        }
      })
      .join('\n');
    
    const printWindow = window.open('', '_blank');
    const content = printRef.current.innerHTML;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Laporan Shift - ${shift?.full_name || ''}</title>
          <meta charset="UTF-8">
          <style>
            /* Copy all existing styles including Tailwind */
            ${stylesheets}
          </style>
          <style>
            @page {
              size: A4;
              margin: 15mm;
            }
            
            * {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            
            body {
              margin: 0;
              padding: 0;
              background: white;
            }
            
            /* Hide print-only header from screen (akan ditampilkan di print) */
            .print-only {
              display: block !important;
            }
            
            /* Ensure proper layout */
            .fixed, .inset-0, .bg-black, .bg-opacity-50, .z-50 {
              position: static !important;
              background: white !important;
            }
            
            /* Remove shadows and borders */
            .shadow-2xl, .rounded-lg {
              box-shadow: none !important;
            }
            
            /* Proper spacing */
            .p-6, .p-5, .p-4 {
              page-break-inside: avoid;
            }
            
            /* Header Styles */
            .header {
              text-align: center;
              padding: 20px 0;
              border-bottom: 3px solid #2563eb;
              margin-bottom: 25px;
            }
            
            .header h1 {
              margin: 0 0 5px 0;
              font-size: 24px;
              font-weight: 700;
              color: #1e40af;
              text-transform: uppercase;
              letter-spacing: 1px;
            }
            
            .header .subtitle {
              font-size: 14px;
              color: #6b7280;
              margin: 5px 0;
            }
            
            .header .date-print {
              font-size: 12px;
              color: #9ca3af;
              margin-top: 8px;
            }
            
            /* Section Styles */
            .section {
              margin: 18px 0;
              page-break-inside: avoid;
            }
            
            .section-title {
              font-size: 15px;
              font-weight: 700;
              color: #1f2937;
              margin-bottom: 10px;
              padding-bottom: 6px;
              border-bottom: 2px solid #e5e7eb;
              display: flex;
              align-items: center;
              gap: 8px;
              page-break-after: avoid;
            }
            
            @media print {
              body {
                margin: 15px !important;
              }
              
              .section {
                margin: 12px 0 !important;
              }
              
              .section-title {
                font-size: 13px !important;
                margin-bottom: 6px !important;
                padding-bottom: 3px !important;
              }
              
              .info-grid {
                gap: 8px !important;
                margin: 10px 0 !important;
              }
              
              .info-item {
                padding: 6px 8px !important;
              }
              
              .info-label {
                font-size: 9px !important;
                margin-bottom: 3px !important;
              }
              
              .info-value {
                font-size: 11px !important;
              }
              
              .header {
                padding: 12px 0 !important;
                margin-bottom: 15px !important;
              }
              
              .header h1 {
                font-size: 20px !important;
                margin: 0 0 4px 0 !important;
              }
              
              .header .subtitle {
                font-size: 12px !important;
                margin: 3px 0 !important;
              }
              
              .header .date-print {
                font-size: 10px !important;
                margin-top: 4px !important;
              }
              
              .cash-section {
                padding: 12px !important;
                margin: 15px 0 !important;
              }
              
              .cash-row {
                padding: 6px 0 !important;
              }
              
              .cash-label {
                font-size: 11px !important;
              }
              
              .cash-value {
                font-size: 12px !important;
              }
              
              .cash-total {
                padding: 8px !important;
                margin-top: 8px !important;
              }
              
              .cash-total .label {
                font-size: 13px !important;
              }
              
              .cash-total .value {
                font-size: 16px !important;
              }
              
              .cash-difference {
                padding: 8px !important;
                margin-top: 8px !important;
              }
              
              .cash-difference-label {
                font-size: 11px !important;
                margin-bottom: 3px !important;
              }
              
              .cash-difference-value {
                font-size: 16px !important;
              }
              
              .revenue-grid {
                gap: 8px !important;
                margin: 10px 0 !important;
              }
              
              .revenue-card {
                padding: 8px !important;
              }
              
              table {
                margin: 10px 0 !important;
              }
              
              th, td {
                padding: 4px 6px !important;
                font-size: 10px !important;
              }
              
              /* Compact footer */
              .footer {
                margin-top: 15px !important;
                padding-top: 8px !important;
              }
            }
            
            /* Info Grid */
            .info-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 12px;
              margin: 15px 0;
              page-break-inside: avoid;
            }
            
            .info-item {
              padding: 10px;
              background: #f9fafb;
              border-radius: 6px;
              border-left: 3px solid #3b82f6;
              page-break-inside: avoid;
            }
            
            .info-label {
              font-size: 10px;
              color: #6b7280;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              margin-bottom: 4px;
              font-weight: 600;
            }
            
            .info-value {
              font-size: 13px;
              color: #111827;
              font-weight: 600;
            }
            
            /* Cash Reconciliation Highlight */
            .cash-section {
              background: #eff6ff;
              border: 2px solid #3b82f6;
              border-radius: 8px;
              padding: 20px;
              margin: 20px 0;
            }
            
            .cash-row {
              display: flex;
              justify-content: space-between;
              padding: 10px 0;
              border-bottom: 1px solid #bfdbfe;
            }
            
            .cash-row:last-child {
              border-bottom: none;
            }
            
            .cash-label {
              font-size: 13px;
              color: #1e40af;
              font-weight: 500;
            }
            
            .cash-value {
              font-size: 14px;
              color: #1e3a8a;
              font-weight: 700;
            }
            
            .cash-total {
              background: #dbeafe;
              padding: 12px;
              border-radius: 6px;
              margin-top: 10px;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            
            .cash-total .label {
              font-size: 15px;
              font-weight: 700;
              color: #1e40af;
            }
            
            .cash-total .value {
              font-size: 18px;
              font-weight: 800;
              color: #1e3a8a;
            }
            
            .cash-difference {
              background: #dcfce7;
              border: 2px solid #16a34a;
              padding: 12px;
              border-radius: 6px;
              margin-top: 10px;
              text-align: center;
            }
            
            .cash-difference.warning {
              background: #fee2e2;
              border-color: #dc2626;
            }
            
            .cash-difference-label {
              font-size: 12px;
              color: #15803d;
              font-weight: 600;
              margin-bottom: 4px;
            }
            
            .cash-difference.warning .cash-difference-label {
              color: #991b1b;
            }
            
            .cash-difference-value {
              font-size: 20px;
              font-weight: 800;
              color: #16a34a;
            }
            
            .cash-difference.warning .cash-difference-value {
              color: #dc2626;
            }
            
            /* Revenue Cards */
            .revenue-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 12px;
              margin: 15px 0;
              page-break-inside: avoid;
            }
            
            .revenue-card {
              background: #f9fafb;
              border: 1px solid #e5e7eb;
              border-radius: 6px;
              padding: 12px;
              text-align: center;
              page-break-inside: avoid;
            }
              }
              
              .revenue-card {
                padding: 10px;
                border-radius: 4px;
              }
            }
            
            .revenue-card.primary {
              background: #eff6ff;
              border-color: #3b82f6;
            }
            
            .revenue-label {
              font-size: 11px;
              color: #6b7280;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              margin-bottom: 8px;
              font-weight: 600;
            }
            
            .revenue-value {
              font-size: 18px;
              font-weight: 800;
              color: #1e3a8a;
            }
            
            .revenue-card.ps .revenue-value {
              color: #1d4ed8;
            }
            
            .revenue-card.fnb .revenue-value {
              color: #ea580c;
            }
            
            .revenue-detail {
              font-size: 10px;
              color: #9ca3af;
              margin-top: 4px;
            }
            
            /* Payment Breakdown */
            .payment-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 12px;
              margin: 15px 0;
              padding: 15px;
              background: #fafafa;
              border-radius: 8px;
            }
            
            .payment-item {
              text-align: center;
              padding: 10px;
              background: white;
              border-radius: 6px;
              border: 1px solid #e5e7eb;
            }
            
            .payment-method {
              font-size: 20px;
              margin-bottom: 5px;
            }
            
            .payment-label {
              font-size: 11px;
              color: #6b7280;
              margin-bottom: 5px;
              font-weight: 600;
            }
            
            .payment-value {
              font-size: 14px;
              font-weight: 700;
              color: #111827;
            }
            
            /* Transaction Table */
            .transaction-table,
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 15px 0;
              font-size: 10px;
              page-break-inside: auto;
            }
            
            .transaction-table th,
            table thead th {
              background: #f3f4f6 !important;
              padding: 8px 6px;
              text-align: left;
              font-weight: 700;
              color: #374151;
              border: 1px solid #d1d5db;
              font-size: 9px;
              text-transform: uppercase;
              letter-spacing: 0.3px;
              white-space: nowrap;
            }
            
            .transaction-table td,
            table tbody td {
              padding: 6px 4px;
              border: 1px solid #e5e7eb;
              color: #111827;
              font-size: 9px;
              vertical-align: middle;
            }
            
            .transaction-table tbody tr:nth-child(even),
            table tbody tr:nth-child(even) {
              background: #f9fafb;
            }
            
            table tbody tr {
              page-break-inside: avoid;
              page-break-after: auto;
            }
            
            /* Ensure table fits on page */
            @media print {
              table {
                font-size: 9px !important;
              }
              
              table th,
              table td {
                padding: 4px 3px !important;
                font-size: 8px !important;
              }
              
              .overflow-x-auto {
                overflow: visible !important;
              }
            }
            
            tr:last-child td {
              border-bottom: none;
            }
            
            .badge {
              display: inline-block;
              padding: 3px 8px;
              border-radius: 4px;
              font-size: 10px;
              font-weight: 600;
              text-transform: uppercase;
            }
            
            .badge-ps {
              background: #dbeafe;
              color: #1e40af;
            }
            
            .badge-fnb {
              background: #fed7aa;
              color: #c2410c;
            }
            
            /* Footer */
            .footer {
              margin-top: 30px;
              padding-top: 15px;
              border-top: 2px solid #e5e7eb;
              text-align: center;
            }
            
            .footer-text {
              font-size: 11px;
              color: #9ca3af;
              line-height: 1.8;
            }
            
            .footer-logo {
              font-size: 13px;
              font-weight: 700;
              color: #2563eb;
              margin-top: 8px;
            }
            
            /* Signature Box */
            .signature-section {
              margin-top: 40px;
              display: flex;
              justify-content: space-between;
              gap: 30px;
            }
            
            .signature-box {
              flex: 1;
              text-align: center;
            }
            
            .signature-label {
              font-size: 12px;
              color: #6b7280;
              margin-bottom: 50px;
            }
            
            .signature-line {
              border-top: 1px solid #374151;
              padding-top: 8px;
              font-size: 12px;
              font-weight: 600;
              color: #111827;
            }
            
            /* Utilities */
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .font-bold { font-weight: 700; }
            .mt-10 { margin-top: 10px; }
            .mb-10 { margin-bottom: 10px; }
            
            /* Page Break Control */
            .page-break-before { page-break-before: always; }
            .no-page-break { page-break-inside: avoid; }
          </style>
        </head>
        <body>
          <div class="print-container">
            ${content}
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 300);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Clock className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Laporan Shift</h2>
              <p className="text-blue-100 text-sm mt-1">Detail lengkap & rekonsiliasi kas</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : reportData ? (
            <div ref={printRef}>
              {/* Print Header (hidden on screen) */}
              <div className="print-only" style={{ display: 'none' }}>
                <div style={{ textAlign: 'center', borderBottom: '3px solid #2563eb', paddingBottom: '20px', marginBottom: '25px' }}>
                  {/* Business Name */}
                  {businessProfile && businessProfile.business_name && (
                    <h1 style={{ fontSize: '26px', fontWeight: '800', color: '#1e40af', margin: '0 0 5px 0', textTransform: 'uppercase' }}>
                      {businessProfile.business_name}
                    </h1>
                  )}
                  
                  {/* Branch Name */}
                  {shift?.branch && (
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#374151', margin: '5px 0' }}>
                      {shift.branch}
                    </div>
                  )}
                  
                  <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1e40af', margin: '10px 0 5px 0' }}>LAPORAN SHIFT KASIR</h2>
                  <div style={{ fontSize: '12px', color: '#9ca3af', margin: '5px 0' }}>Powered by Rimuru Game Suite</div>
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>Dicetak: {formatDateTime(new Date())}</div>
                </div>
              </div>

              {/* Shift Information */}
              <div className="mb-6 bg-gray-50 rounded-lg p-5">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <User className="h-5 w-5 text-blue-600" />
                  Informasi Shift
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Nama Kasir</p>
                    <p className="font-semibold text-gray-900">{shift?.full_name}</p>
                    <p className="text-xs text-gray-500">@{shift?.username}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Cabang</p>
                    <p className="font-semibold text-gray-900">{shift?.branch || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Waktu Mulai</p>
                    <p className="font-semibold text-gray-900">{formatDateTime(shift?.start_time)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Waktu Selesai</p>
                    <p className="font-semibold text-gray-900">
                      {shift?.end_time ? formatDateTime(shift.end_time) : <span className="text-green-600">Masih Aktif</span>}
                    </p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-sm text-gray-500 mb-1">Durasi Shift</p>
                    <p className="font-semibold text-gray-900">{calculateDuration(shift?.start_time, shift?.end_time)}</p>
                  </div>
                </div>
              </div>

              {/* Cash Reconciliation */}
              {shift?.status === 'closed' && (
                <div className="mb-6 bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200 rounded-lg p-5">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-green-600" />
                    Rekonsiliasi Kas
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-700">Kas Awal (Starting Cash)</span>
                      <span className="font-semibold text-gray-900">{formatCurrency(shift.starting_cash || 0)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-700">Total Penjualan (Cash)</span>
                      <span className="font-semibold text-green-600">+ {formatCurrency(reportData?.paymentBreakdown?.cash || 0)}</span>
                    </div>
                    <div className="flex justify-between items-center pt-3 border-t-2 border-blue-300">
                      <span className="font-bold text-gray-900">Kas yang Diharapkan</span>
                      <span className="text-xl font-bold text-blue-600">
                        {formatCurrency((shift.starting_cash || 0) + (reportData?.paymentBreakdown?.cash || 0))}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-3 border-t border-blue-200">
                      <span className="font-semibold text-gray-900">Kas Aktual (Hitung Fisik)</span>
                      <span className="font-semibold text-gray-900">{formatCurrency(shift.ending_cash || 0)}</span>
                    </div>
                    
                    {(() => {
                      // CRITICAL FIX: Recalculate cash difference, don't use stored data!
                      const expectedCash = (shift.starting_cash || 0) + (reportData?.paymentBreakdown?.cash || 0);
                      const actualCash = shift.ending_cash || 0;
                      const cashDifference = actualCash - expectedCash;
                      
                      return (
                        <div className={`rounded-lg p-4 text-center ${
                          Math.abs(cashDifference) < 1000 
                            ? 'bg-green-50 border-2 border-green-500' 
                            : 'bg-red-50 border-2 border-red-500'
                        }`}>
                          <div className={`text-sm font-semibold mb-2 ${
                            Math.abs(cashDifference) < 1000 ? 'text-green-700' : 'text-red-700'
                          }`}>
                            {Math.abs(cashDifference) < 1000 ? ' Selisih Kas (Dalam Toleransi)' : ' Selisih Kas (Perlu Verifikasi)'}
                          </div>
                          <div className={`text-2xl font-bold ${
                            Math.abs(cashDifference) < 1000 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {cashDifference >= 0 ? '+ ' : '- '}
                            {formatCurrency(Math.abs(cashDifference))}
                          </div>
                          <div className="text-xs mt-2 opacity-80">
                            {cashDifference > 0 ? 'Kas Lebih (Surplus)' : cashDifference < 0 ? 'Kas Kurang (Minus)' : 'Kas Sesuai'}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Revenue Summary */}
              <div className="mb-6 bg-white border border-gray-200 rounded-lg p-5">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                  Ringkasan Pendapatan
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Gamepad2 className="h-5 w-5 text-blue-600" />
                      <p className="text-sm text-gray-600 font-medium">PS Rental</p>
                    </div>
                    <p className="text-2xl font-bold text-blue-600">{formatCurrency(reportData.summary.psRevenue)}</p>
                    <p className="text-xs text-gray-500 mt-1">{reportData.summary.psTransactions} transaksi</p>
                  </div>

                  <div className="bg-orange-50 rounded-lg p-4 text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <ShoppingCart className="h-5 w-5 text-orange-600" />
                      <p className="text-sm text-gray-600 font-medium">F&B Sales</p>
                    </div>
                    <p className="text-2xl font-bold text-orange-600">{formatCurrency(reportData.summary.fnbRevenue)}</p>
                    <p className="text-xs text-gray-500 mt-1">{reportData.summary.fnbTransactions} transaksi</p>
                  </div>

                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <DollarSign className="h-5 w-5 text-green-600" />
                      <p className="text-sm text-gray-600 font-medium">Total</p>
                    </div>
                    <p className="text-2xl font-bold text-green-600">{formatCurrency(reportData.summary.totalRevenue)}</p>
                    <p className="text-xs text-gray-500 mt-1">{reportData.summary.totalTransactions} transaksi</p>
                  </div>
                </div>

                {/* Payment Method Breakdown */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-semibold text-gray-700 mb-3">Metode Pembayaran</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-white rounded-lg p-3 text-center border border-gray-200">
                      <div className="text-2xl mb-1"></div>
                      <div className="text-xs text-gray-600 mb-1">Cash</div>
                      <div className="font-semibold text-sm text-gray-900">{formatCurrency(reportData.paymentBreakdown.cash)}</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center border border-gray-200">
                      <div className="text-2xl mb-1"></div>
                      <div className="text-xs text-gray-600 mb-1">QRIS</div>
                      <div className="font-semibold text-sm text-gray-900">{formatCurrency(reportData.paymentBreakdown.qris)}</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center border border-gray-200">
                      <div className="text-2xl mb-1"></div>
                      <div className="text-xs text-gray-600 mb-1">Transfer</div>
                      <div className="font-semibold text-sm text-gray-900">{formatCurrency(reportData.paymentBreakdown.transfer)}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* PS Packages Breakdown */}
              {reportData.psPackagesBreakdown && Object.keys(reportData.psPackagesBreakdown).length > 0 && (
                <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-5">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Gamepad2 className="h-5 w-5 text-blue-600" />
                    Detail Penjualan Rental PS
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(reportData.psPackagesBreakdown)
                      .sort((a, b) => b[1].revenue - a[1].revenue)
                      .map(([packageName, data]) => (
                        <div key={packageName} className="flex justify-between items-center bg-white rounded-lg p-4 border border-blue-100">
                          <div className="flex items-center gap-3 flex-1">
                            <div className="bg-blue-100 rounded-full w-12 h-12 flex items-center justify-center">
                              <span className="text-blue-700 font-bold">{data.qty}x</span>
                            </div>
                            <div>
                              <p className="font-bold text-gray-900">{packageName}</p>
                              <p className="text-sm text-gray-600">
                                {data.qty} rental • Total {data.totalHours.toFixed(1)} jam
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-blue-600 text-lg">{formatCurrency(data.revenue)}</p>
                            <p className="text-xs text-gray-500">{formatCurrency(data.revenue / data.qty)}/rental</p>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* F&B Items Breakdown */}
              {reportData.fnbItemsBreakdown && Object.keys(reportData.fnbItemsBreakdown).length > 0 && (
                <div className="mb-6 bg-orange-50 border border-orange-200 rounded-lg p-5">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <ShoppingCart className="h-5 w-5 text-orange-600" />
                    Detail Penjualan F&B
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(reportData.fnbItemsBreakdown)
                      .sort((a, b) => b[1].revenue - a[1].revenue)
                      .map(([itemName, data]) => (
                        <div key={itemName} className="flex justify-between items-center bg-white rounded-lg p-3 border border-orange-100">
                          <div className="flex items-center gap-3">
                            <div className="bg-orange-100 rounded-full w-10 h-10 flex items-center justify-center">
                              <span className="text-orange-700 font-bold text-sm">{data.qty}</span>
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{itemName}</p>
                              <p className="text-xs text-gray-500">{data.qty} item</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-orange-600">{formatCurrency(data.revenue)}</p>
                            <p className="text-xs text-gray-500">{formatCurrency(data.revenue / data.qty)}/item</p>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Transaction List */}
              {reportData.transactions.length > 0 && (
                <div className="mb-6 bg-white border border-gray-200 rounded-lg p-5">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-purple-600" />
                    Daftar Transaksi ({reportData.transactions.length})
                  </h3>
                  
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Waktu</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipe</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Detail</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pembayaran</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {reportData.transactions.slice(0, 50).map((transaction, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">{formatTime(transaction.transaction_date)}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                transaction.transaction_type === 'ps' 
                                  ? 'bg-blue-100 text-blue-700' 
                                  : 'bg-orange-100 text-orange-700'
                              }`}>
                                {transaction.transaction_type === 'ps' ? 'PS' : 'F&B'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {transaction.transaction_type === 'ps' ? (
                                <div className="space-y-1">
                                  <div className="font-medium">{transaction.ps_unit_name || 'PS Unit'}</div>
                                  {transaction.additional_items && transaction.additional_items.length > 0 && (
                                    <div className="text-xs text-gray-600">
                                      {transaction.additional_items.map((item, itemIdx) => (
                                        <div key={itemIdx}>
                                          <span className="font-medium">{item.qty || item.quantity || 1}x</span>{' '}
                                          <span>{item.name || item.product_name || 'Item'}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="space-y-1">
                                  {transaction.items && transaction.items.length > 0 ? (
                                    transaction.items.map((item, itemIdx) => (
                                      <div key={itemIdx} className="text-xs">
                                        <span className="font-medium">{item.quantity || item.qty || 1}x</span>{' '}
                                        <span>{item.name || item.product_name || 'Item'}</span>
                                      </div>
                                    ))
                                  ) : (
                                    <span>{transaction.items?.length || 0} item</span>
                                  )}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 capitalize">
                              {transaction.payment_method || 'cash'}
                            </td>
                            <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">
                              {formatCurrency(transaction.transaction_type === 'ps' 
                                ? (transaction.final_price || transaction.total_price || 0)
                                : (transaction.total || transaction.final_price || transaction.total_price || 0)
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {reportData.transactions.length > 50 && (
                    <p className="text-xs text-gray-500 mt-3 text-center">
                      Menampilkan 50 dari {reportData.transactions.length} transaksi
                    </p>
                  )}
                </div>
              )}

              {/* Signature Section - Print Only */}
              <div className="print-only" style={{ display: 'none' }}>
                <div style={{ marginTop: '40px', display: 'flex', justifyContent: 'space-between', gap: '30px' }}>
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '50px' }}>Kasir</div>
                    <div style={{ borderTop: '1px solid #374151', paddingTop: '8px', fontSize: '12px', fontWeight: '600', color: '#111827' }}>
                      {shift?.full_name}
                    </div>
                  </div>
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '50px' }}>Supervisor / Manager</div>
                    <div style={{ borderTop: '1px solid #374151', paddingTop: '8px', fontSize: '12px', fontWeight: '600', color: '#111827' }}>
                      ( _________________ )
                    </div>
                  </div>
                </div>
              </div>

              {/* Print Footer */}
              <div className="print-only" style={{ display: 'none' }}>
                <div style={{ marginTop: '30px', paddingTop: '15px', borderTop: '2px solid #e5e7eb', textAlign: 'center' }}>
                  <div style={{ fontSize: '11px', color: '#9ca3af', lineHeight: '1.8' }}>
                    Laporan ini dicetak pada {formatDateTime(new Date())}<br/>
                    Dokumen resmi Rimuru Game Suite - Sistem Rental PlayStation
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#2563eb', marginTop: '8px' }}>
                    RIMURU GAME SUITE
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer Actions */}
        <div className="border-t border-gray-200 p-4 bg-gray-50 flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition"
          >
            Tutup
          </button>
          <button
            onClick={handlePrint}
            disabled={loading || !reportData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Printer className="h-4 w-4" />
            Cetak Laporan
          </button>
        </div>
      </div>
    </div>
  );
}
